import { createNode, updateNode, deleteNode, createEdge, updateEdge, deleteEdge } from './graph.utils';
import {
    createLocalNodeMessage,
    createLocalNodeCondition,
    createLocalEdges,
    buildEdge,
    createExitNode,
} from '@/orchestrate/journeys/helpers';
import remove from 'lodash/remove';
import { BRANCHING_NODES_TYPES, BRANCHING_EDGES_TYPES, MESSAGE_TYPES } from '@/orchestrate/journeys/constants';
import { PROVIDER_TYPES } from '@/orchestrate/journeys/integrations.constants';
import { syncEntitiesFromProvidersByJourneyId } from '../journeys.utils';
import { LOADING_STATES } from '@/constants';
import { patchGraph } from './graph.logic';
import { PATCH_TYPE } from '@/orchestrate/journeys/graph.constants';
import { PendoNotification } from '@pendo/components';

const { MESSAGE, CONDITION, START, EXIT } = BRANCHING_NODES_TYPES;
const { REGULAR } = BRANCHING_EDGES_TYPES;
const { EMAIL } = MESSAGE_TYPES;
const { HUBSPOT, ELOQUA } = PROVIDER_TYPES;
const { RESOLVED } = LOADING_STATES;
const { ADD, REMOVE } = PATCH_TYPE;

const graph = {
    namespaced: true,
    state: {
        error: null,
        activeConditionId: null,
        nodesStaticSegmentCountAggData: {},
    },
    mutations: {
        setError (state, { error }) {
            state.error = error;
        },
        setActiveCondition (state, { conditionId }) {
            state.activeConditionId = conditionId;
        },
        setNodesStaticSegmentCountAggData (state, { status, result = null }) {
            const payload = { status };

            if (status === RESOLVED && Array.isArray(result)) {
                payload.result = Object.assign({}, ...result);
            }
            state.nodesStaticSegmentCountAggData = payload;
        },
    },
    actions: {
        async setGraphNodes ({ rootGetters, dispatch }, { nodes }) {
            const journey = rootGetters['journeys/getActiveJourney'];
            try {
                const savedNodes = [];
                for (let i = 0; i < nodes.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    const savedNode = await dispatch('createNode', { journeyId: journey.id, node: nodes[i] });
                    savedNodes.push(savedNode);
                }

                const messageGraph = {
                    ...(journey.messageGraph || {}),
                    nodes: savedNodes,
                };

                dispatch('journeys/setJourneyGraph', { messageGraph }, { root: true });
            } catch (ex) {
                throw new Error(`create nodes unsuccessful: ${ex.message}`);
            }
        },
        async setGraphEdges ({ rootGetters, dispatch }, { edges }) {
            const journey = rootGetters['journeys/getActiveJourney'];
            try {
                const savedEdges = [];
                for (let i = 0; i < edges.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    const savedEdge = await dispatch('createEdge', { journeyId: journey.id, edge: edges[i] });
                    savedEdges.push(savedEdge);
                }

                const messageGraph = {
                    ...(journey.messageGraph || {}),
                    edges: savedEdges,
                };

                dispatch('journeys/setJourneyGraph', { messageGraph }, { root: true });
            } catch (ex) {
                throw new Error(`create edges unsuccessful: ${ex.message}`);
            }
        },
        async addEdgeAndExitNode ({ rootGetters, dispatch, getters }, { originNodeId, connectToEdgeType }) {
            const journey = rootGetters['journeys/getActiveJourney'];
            const nodes = [...journey.messageGraph.nodes];
            const edges = [...journey.messageGraph.edges];

            const originNode = getters.getNodeById(originNodeId);
            if (!originNode) {
                throw new Error('Node not found.');
            }

            try {
                const exitNode = createExitNode();
                const savedExitNode = await dispatch('createNode', { journeyId: journey.id, node: exitNode });
                nodes.push(savedExitNode);

                const newLocalEdge = createLocalEdges([originNode, savedExitNode], [connectToEdgeType])[0];
                const savedEdge = await dispatch('createEdge', { journeyId: journey.id, edge: newLocalEdge });
                edges.push(savedEdge);
            } catch (ex) {
                throw new Error(`Add edge and exit node were unsuccessful. ${ex}`);
            }

            /* update the local message graph with new node and edge */
            const updatedMessageGraph = { ...journey.messageGraph, nodes, edges };
            dispatch('journeys/setJourneyGraph', { messageGraph: updatedMessageGraph }, { root: true });
        },
        async insertNodeInsteadOfEdge ({ rootGetters, dispatch }, { nodeType, payload, edgeId, connectToEdgeType }) {
            const journey = rootGetters['journeys/getActiveJourney'];
            const nodes = [...journey.messageGraph.nodes];
            const edges = [...journey.messageGraph.edges];

            /* create new node */
            let localNode;
            if (nodeType === MESSAGE) {
                localNode = createLocalNodeMessage(payload);
            } else if (nodeType === CONDITION) {
                localNode = createLocalNodeCondition(payload);
            }

            const savedNode = await dispatch('createNode', { journeyId: journey.id, node: localNode });
            nodes.push(savedNode);

            /* load and set in the store the newly created email */
            if (savedNode?.message?.type === EMAIL) {
                if ([HUBSPOT, ELOQUA].includes(savedNode.message.provider)) {
                    try {
                        await syncEntitiesFromProvidersByJourneyId(journey.id);
                        await dispatch('emails/loadOne', { id: savedNode.message.id, setActive: true }, { root: true });
                    } catch (error) {
                        PendoNotification({
                            type: 'error',
                            title: 'Error',
                            message: `Failed to sync external provider data: ${error.message}`,
                        });
                    }
                } else {
                    await dispatch('emails/loadOne', { id: savedNode.message.id, setActive: true }, { root: true });
                }
            }

            /* delete the edge, but first remember the 2 nodes it connects to */
            const edgeToDelete = edges.find((edge) => edge.id === edgeId);
            const { from, to } = edgeToDelete;
            const nodeFrom = nodes.find((node) => node.id === from);
            const nodeTo = nodes.find((node) => node.id === to);
            await dispatch('deleteEdge', { journeyId: journey.id, edgeId });
            remove(edges, (edge) => edge.id === edgeId);

            /* create 2 new edges to connect 3 nodes (the new node is in the middle) */
            const edgeTypes = [edgeToDelete.edgeType, nodeType === CONDITION ? connectToEdgeType : REGULAR];
            const newLocalEdges = createLocalEdges([nodeFrom, savedNode, nodeTo], edgeTypes);
            const edgeFrom = await dispatch('createEdge', { journeyId: journey.id, edge: newLocalEdges[0] });
            const edgeTo = await dispatch('createEdge', { journeyId: journey.id, edge: newLocalEdges[1] });
            edges.push(...[edgeFrom, edgeTo]);

            /* update the local message graph with new node and new edges */
            const updatedMessageGraph = { ...journey.messageGraph, nodes, edges };
            dispatch('journeys/setJourneyGraph', { messageGraph: updatedMessageGraph }, { root: true });

            return savedNode.id;
        },
        async updateAndSetNode ({ rootGetters, dispatch }, { nodeToUpdate }) {
            const journey = rootGetters['journeys/getActiveJourney'];
            const nodes = [...journey.messageGraph.nodes];

            try {
                if (!nodeToUpdate) {
                    throw new Error('Node is empty.');
                }

                if (
                    nodeToUpdate.nodeType === CONDITION ||
                    (nodeToUpdate.nodeType === MESSAGE && nodeToUpdate.audience && nodeToUpdate.audienceUiHint)
                ) {
                    const { audience, audienceUiHint, ...rest } = nodeToUpdate;
                    nodeToUpdate = {
                        ...rest,
                        audienceData: {
                            audience,
                            audienceUiHint,
                        },
                    };
                }

                const updatedNode = await dispatch('updateNode', { journeyId: journey.id, updatedNode: nodeToUpdate });
                const index = nodes.findIndex((node) => node.id === updatedNode.id);
                nodes.splice(index, 1, updatedNode);
            } catch (ex) {
                throw new Error(`Update node were unsuccessful. ${ex}`);
            }

            /* update the local message graph with new node and new edges */
            const updatedMessageGraph = { ...journey.messageGraph, nodes };
            dispatch('journeys/setJourneyGraph', { messageGraph: updatedMessageGraph }, { root: true });
        },
        async createNode ({ commit }, { journeyId, node }) {
            try {
                return await createNode(journeyId, node);
            } catch (ex) {
                const error = new Error(`create node unsuccessful: ${ex.message}`);
                commit('setError', { error });
                throw error;
            }
        },
        async updateNode ({ commit }, { journeyId, updatedNode }) {
            try {
                commit('journeys/setEditing', { editing: true }, { root: true });

                return await updateNode(journeyId, updatedNode);
            } catch (ex) {
                const error = new Error(`update node unsuccessful: ${ex.message}`);
                commit('setError', { error });
                throw error;
            } finally {
                commit('journeys/setEditing', { editing: false }, { root: true });
            }
        },
        async deleteNode ({ commit }, { journeyId, nodeId }) {
            try {
                await deleteNode(journeyId, nodeId);
            } catch (ex) {
                const error = new Error(`delete node unsuccessful: ${ex.message}`);
                commit('setError', { error });
                throw error;
            }
        },
        async createEdge ({ commit }, { journeyId, edge }) {
            try {
                return await createEdge(journeyId, edge);
            } catch (ex) {
                const error = new Error(`create edge unsuccessful: ${ex.message}`);
                commit('setError', { error });
                throw error;
            }
        },
        async updateEdge ({ commit }, { journeyId, updatedEdge }) {
            try {
                return await updateEdge(journeyId, updatedEdge);
            } catch (ex) {
                const error = new Error(`update edge unsuccessful: ${ex.message}`);
                commit('setError', { error });
                throw error;
            }
        },
        async deleteEdge ({ commit }, { journeyId, edgeId }) {
            try {
                await deleteEdge(journeyId, edgeId);
            } catch (ex) {
                const error = new Error(`delete edge unsuccessful: ${ex.message}`);
                commit('setError', { error });
                throw error;
            }
        },
        async deleteNodeAndUpdateEdges ({ rootGetters, getters, dispatch, commit }, { node }) {
            if (!node) {
                throw new Error('Node is empty.');
            }

            if (node.nodeType === START || node.nodeType === EXIT) {
                throw new Error('Cannot delete Start or Exit node.');
            }

            const journey = rootGetters['journeys/getActiveJourney'];
            const journeyId = journey.id;
            let nodes = [...journey.messageGraph.nodes];
            let edges = [...journey.messageGraph.edges];

            const nodeToDelete = nodes.find((nd) => nd.id === node?.id);

            if (!nodeToDelete) {
                throw new Error('Node not found in the active journey.');
            }

            // node is part of the active journey's graph
            const edgeBeforeNode = edges.find((edge) => edge.to === node.id);
            const edgesAfterNode = edges.filter((edge) => edge.from === node.id);

            if (!edgeBeforeNode || !edgesAfterNode.length) {
                throw new Error('Could not find both edges connected to the node.');
            }

            /* if MESSAGE node, edgesAfterNode will have 1 edge
                if CONDITION, there is 2 exit nodes, one should stay, the other one go */
            const newEdge = buildEdge({
                edgeType: edgeBeforeNode.edgeType,
                from: edgeBeforeNode.from,
                to: edgesAfterNode[0].to,
            });

            try {
                await dispatch('deleteEdge', { journeyId, edgeId: edgeBeforeNode.id });
                for (const edge of edgesAfterNode) {
                    // eslint-disable-next-line no-await-in-loop
                    await dispatch('deleteEdge', { journeyId, edgeId: edge.id });
                }
                await dispatch('deleteNode', { journeyId, nodeId: nodeToDelete.id });
                // reset active message
                commit('journeys/setCurrentMessageId', { messageId: null }, { root: true });

                if (nodeToDelete.nodeType === MESSAGE && nodeToDelete.message.type === EMAIL) {
                    // update emails/list immediately since it uses local state
                    // no need to update guides/list since it will refetch via aggregation on guides table
                    commit('emails/removeEmail', { id: nodeToDelete.message.id }, { root: true });
                }

                const builtEdge = await dispatch('createEdge', { journeyId, edge: newEdge });

                // update local graph
                nodes = nodes.filter((node) => !(node.id === nodeToDelete.id));
                edges = edges.filter((edge) => {
                    return ![edgeBeforeNode.id, ...edgesAfterNode.map((edgeAfterNode) => edgeAfterNode.id)].includes(
                        edge.id,
                    );
                });

                edges.push(builtEdge);

                /* if the node to delete is conditional then we have 2 exit nodes - delete one */
                if (edgesAfterNode.length > 1) {
                    const exitNodeToDelete = getters.getNodeById(edgesAfterNode[1].to);
                    await dispatch('deleteNode', { journeyId, nodeId: exitNodeToDelete.id });
                    nodes = nodes.filter((node) => exitNodeToDelete.id !== node.id);
                }

                /* update the local message graph with new node and new edges */
                const updatedMessageGraph = { ...journey.messageGraph, nodes, edges };
                dispatch('journeys/setJourneyGraph', { messageGraph: updatedMessageGraph }, { root: true });
            } catch (error) {
                throw new Error(`Delete node and edges unsuccessful: ${error.message}`);
            }
        },
        async addEdgeAndDeleteExitNode (
            { rootGetters, commit, dispatch },
            { edgeToDelete, edgeToAdd },
        ) {
            commit('setError', { error: null });

            const journey = rootGetters['journeys/getActiveJourney'];
            const messageGraph = journey?.messageGraph;
            const nodes = [...messageGraph.nodes];

            // find the actual exit node
            const exitNode = nodes.find((node) => node.id === edgeToDelete.to);
            if (!exitNode || exitNode.nodeType !== EXIT) dispatch('commitAndThrowError', { message: 'Could not delete exit node.' });

            // build the new edge
            const newEdge = buildEdge({
                ...edgeToAdd,
                ...(edgeToAdd.isManual ? { attributes: { isManual: true } } : {}),
            });

            let savedEdge;

            try {
                // order matters: remove edge -> remove node -> add new edge
                await dispatch('deleteEdge', { journeyId: journey.id, edgeId: edgeToDelete.id });
                await dispatch('deleteNode', { journeyId: journey.id, nodeId: exitNode.id });
                savedEdge = await dispatch('createEdge', { journeyId: journey.id, edge: newEdge });
            } catch (ex) {
                dispatch('commitAndThrowError', { message: `Add edge unsuccessful: ${ex.message}` });
            }

            // update the local message graph
            const updatedMessageGraph = patchGraph({
                messageGraph,
                edgesPatchList: [
                    { edge: savedEdge, patch: ADD },
                    { edge: edgeToDelete, patch: REMOVE },
                ],
                nodesPatchList: [{ node: exitNode, patch: REMOVE }],
            });
            dispatch('journeys/setJourneyGraph', { messageGraph: updatedMessageGraph }, { root: true });

            return savedEdge;
        },
        async deleteManualEdgeAndCreateEdgeWithExitNode ({ rootGetters, dispatch, getters }, { edgeId }) {
            const journey = rootGetters['journeys/getActiveJourney'];
            const edgeToDelete = getters.getEdgeById(edgeId);
            const edges = [...journey.messageGraph.edges];
            const nodes = [...journey.messageGraph.nodes];

            await dispatch('deleteEdge', { journeyId: journey.id, edgeId });
            remove(edges, (edge) => edge.id === edgeId);

            /* update the local message graph with new node and new edges */
            const updatedMessageGraph = { ...journey.messageGraph, nodes, edges };
            dispatch('journeys/setJourneyGraph', { messageGraph: updatedMessageGraph }, { root: true });

            await dispatch('addEdgeAndExitNode', {
                originNodeId: edgeToDelete.from,
                connectToEdgeType: edgeToDelete.edgeType,
            });
        },
        commitAndThrowError ({ commit }, { message }) {
            const error = new Error(message);
            commit('setError', { error });
            throw error;
        },
    },
    getters: {
        getNodeById: (state, getters, rootState, rootGetters) => (nodeId) => {
            const journey = rootGetters['journeys/getActiveJourney'];

            return journey.messageGraph.nodes.find((node) => node.id === nodeId);
        },
        getEdgeById: (state, getters, rootState, rootGetters) => (edgeId) => {
            const journey = rootGetters['journeys/getActiveJourney'];

            return journey.messageGraph.edges.find((edge) => edge.id === edgeId);
        },
        isLeaf: (state, getters, rootState, rootGetters) => (node) => {
            if (!node) return false;

            const journey = rootGetters['journeys/getActiveJourney'];
            const edges = [...journey.messageGraph.edges];
            const edgesAfterNode = edges.filter((edge) => edge.from === node.id);

            return edgesAfterNode.every((edge) => getters.getNodeById(edge.to).nodeType === EXIT);
        },
        getActiveMessageNode: (state, getters, rootState, rootGetters) => {
            const journey = rootGetters['journeys/getActiveJourney'];
            const message = rootGetters['journeys/getActiveMessage'];

            if (message) {
                return journey.messageGraph.nodes.find((node) => node.message?.id === message.id);
            }

            return null;
        },
        getActiveCondition: (state, { getNodeById }) => {
            return getNodeById(state.activeConditionId);
        },
        getNodesStaticSegmentCountAggStatus: (state) => state.nodesStaticSegmentCountAggData.status,
        getNodesStaticSegmentCountAggData: (state) => state.nodesStaticSegmentCountAggData.result,
    },
};

export default graph;
